import React, { useState, useEffect, useContext } from "react";

// MUI
import styled from "@emotion/styled";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

// Icons
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// Contexts
import { UploadContext } from "../../contexts/UploadContext";

const Container = styled("div")(({ theme, minimized }) => ({
  width: "100%",
  borderRadius: "8px",
  "& .heading": {
    padding: "0.5rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "move", // Add cursor move indicator for draggability
    backgroundColor: "#f8f8f8", // Subtle background to indicate draggable area
    "& .text_style": {
      fontWeight: "600",
      fontSize: "18px",
    },
    "& .action-buttons": {
      display: "flex",
      gap: "0.5rem",
    },
  },
  "& .content-container": {
    transition: "max-height 0.3s ease-in-out",
    maxHeight: minimized ? "0" : "300px",
    overflow: "hidden",
  },
  "& .files-list": {
    padding: "0.5rem 1rem",
    maxHeight: "250px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#555",
    },
  },
  "& .file-item": {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: "#f5f5f5",
    gap: "12px",
    position: "relative", // For hover positioning
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "#eaeaea",
    },
    "& .file-preview": {
      width: "32px",
      height: "32px",
      borderRadius: "4px",
      overflow: "hidden",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      fontSize: "20px", // Larger emoji
    },
    "& .file-name": {
      flex: 1,
      fontSize: "14px",
      fontWeight: 500,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      marginRight: "8px",
    },
    "& .status-icon": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "32px",
      height: "32px",
      position: "relative",
    },
    "&.error": {
      backgroundColor: "#ffebee",
    },
    "&.success": {
      backgroundColor: "#e8f5e9",
    },
  },
}));

// Circular progress with label component
function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" {...props} size={28} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          sx={{ fontSize: "9px", fontWeight: "bold" }}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const UploadProgressModal = () => {
  const { uploads, cancelUpload } = useContext(UploadContext);

  const [minimized, setMinimized] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hoveredFile, setHoveredFile] = useState(null);

  const uploadFiles = Object.entries(uploads).map(([fileId, fileInfo]) => ({
    id: fileId,
    ...fileInfo,
  }));

  useEffect(() => {
    if (uploadFiles.length > 0) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [uploadFiles.length]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const uploadingFiles = uploadFiles.filter(
        (file) => file.status === "uploading"
      );

      if (uploadingFiles.length > 0) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [uploadFiles]);

  // Get counts for different upload statuses
  const totalFiles = uploadFiles.length;
  const completedFiles = uploadFiles.filter(
    (f) => f.status === "completed"
  ).length;
  const errorFiles = uploadFiles.filter((f) => f.status === "error").length;
  const uploadingFiles = uploadFiles.filter(
    (f) => f.status === "uploading"
  ).length;

  const getHeadingText = () => {
    if (completedFiles === totalFiles && totalFiles > 0) {
      return `${completedFiles} ${
        completedFiles === 1 ? "file" : "files"
      } uploaded`;
    } else if (errorFiles === totalFiles && totalFiles > 0) {
      return `${errorFiles} ${errorFiles === 1 ? "file" : "files"} failed`;
    } else {
      return `Uploading Files (${completedFiles}/${totalFiles})`;
    }
  };

  const handleToggleMinimize = () => {
    setMinimized((prev) => !prev);
  };

  const handleClose = () => {
    // Only allow closing if no files are being uploaded
    setShowModal(false);
  };

  // Don't render if there's nothing to show
  if (!showModal) {
    return null;
  }

  const getStatusIcon = (file) => {
    if (file.status === "uploading") {
      if (hoveredFile === file.id && file.progress == 0) {
        return (
          <IconButton
            size="small"
            onClick={() => cancelUpload(file.id)}
            sx={{
              width: "28px",
              height: "28px",
              bgcolor: "rgba(0,0,0,0.1)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.2)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        );
      }
      return <CircularProgressWithLabel value={file.progress} />;
    } else if (file.status === "completed") {
      return <CheckCircleIcon color="success" />;
    } else if (file.status === "error") {
      return (
        <Tooltip title={file.error || "Upload failed"} placement="top" arrow>
          <ErrorOutlineIcon color="error" />
        </Tooltip>
      );
    } else {
      return (
        <CircularProgress
          size={28}
          sx={{
            color: "rgba(0, 0, 0, 0.5)", // Optional: Customize the color
          }}
        />
      );
    }
  };

  const getFilePreview = (file) => {
    if (!file || !file.file || !file.file.type) {
      return "📄";
    }

    if (file.file.type.startsWith("image/")) {
      return "🖼️";
    } else if (file.file.type.startsWith("video/")) {
      return "🎬";
    } else if (file.file.type.startsWith("audio/")) {
      return "🎵";
    } else {
      return "📄";
    }
  };

  return (
    <Dialog
      open={showModal}
      maxWidth="sm"
      fullWidth
      hideBackdrop={true}
      disableEnforceFocus={true}
      disableScrollLock={true}
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          position: "fixed",
          bottom: "16px",
          right: "16px",
          m: 0,
          width: { xs: "calc(100% - 32px)", sm: "320px" }, // Narrower width
          maxHeight: "80vh",
          zIndex: 1200,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          border: "1px solid #e0e0e0",
          overflow: "visible",
          borderRadius: "8px",
        },
      }}
      sx={{
        pointerEvents: "none", // Makes the backdrop non-blocking
        "& .MuiDialog-paper": {
          pointerEvents: "auto", // Dialog paper receives events
        },
      }}
    >
      <Container minimized={minimized}>
        <div className="heading">
          <Typography className="text_style">{getHeadingText()}</Typography>
          <div className="action-buttons">
            <IconButton onClick={handleToggleMinimize} size="small">
              {minimized ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <IconButton
              onClick={handleClose}
              size="small"
              disabled={uploadingFiles > 0}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <div className="content-container">
          <div className="files-list">
            {uploadFiles.map((file) => (
              <div
                key={file.id}
                className={`file-item ${
                  file.status === "error"
                    ? "error"
                    : file.status === "completed"
                    ? "success"
                    : ""
                }`}
                onMouseEnter={() => setHoveredFile(file.id)}
                onMouseLeave={() => setHoveredFile(null)}
              >
                <div className="file-preview">{getFilePreview(file)}</div>
                <Typography className="file-name" title={file?.file?.name}>
                  {file?.file?.name}
                </Typography>
                <div className="status-icon">{getStatusIcon(file)}</div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Dialog>
  );
};

export default UploadProgressModal;
